import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTable, useSortBy, usePagination, useFilters, useRowSelect } from "react-table";
import DataNavigationModal from "../DataNavigationModal";
import FilterModal from "../FilterModal/FilterModal";
import "./PatientsTable.css";

const PatientsTable = ({ refreshData, onSelectedIdsChange }) => {
  const token = localStorage.getItem("token");

  const [patients, setPatients] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [placeholder, setPlaceholder] = useState("");

  const [isModalOpen, setModalOpen] = useState(false);
  const [modalResourceId, setModalResourceId] = useState(null);

  const [isFilterModalOpen, setFilterModalOpen] = useState(false);

  const openModalForPatient = (patientId) => {
    setModalResourceId(patientId);
    setModalOpen(true);
  };

  const IndeterminateCheckbox = React.forwardRef(
    ({ indeterminate, ...rest }, ref) => {
      const defaultRef = React.useRef();
      const resolvedRef = ref || defaultRef;
      useEffect(() => {
        resolvedRef.current.indeterminate = indeterminate;
      }, [resolvedRef, indeterminate]);
      return <input type="checkbox" ref={resolvedRef} {...rest} />;
    }
  );

  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
        filterType: "Exact",
      },
      {
        Header: "Hospital Patient ID",
        accessor: "hospital_patient_id",
      },
      {
        Header: "File Path",
        accessor: "file_path",
        Cell: ({ value }) => (
          <a href={value} target="_blank" rel="noreferrer">
            {value}
          </a>
        ),
      },
      {
        Header: "Date of Birth",
        accessor: "date_of_birth",
        filterType: "Exact",
      },
      {
        Header: "Gender",
        accessor: "gender",
      },
      {
        Header: "Data Source",
        accessor: "data_source",
      },
      {
        Header: "Ethnicity",
        accessor: "ethnicity",
      },
      {
        Header: "Diagnosis",
        accessor: "diagnosis",
      },
      {
        Header: "Notes",
        accessor: "notes",
      },
      {
        Header: "Original Upload Path",
        accessor: "original_upload_path",
        Cell: ({ value }) => (
          <a href={value} target="_blank" rel="noreferrer">
            {value}
          </a>
        ),
      },
      {
        Header: "Creation Time",
        accessor: "creation_time",
        Cell: ({ value }) => new Date(value).toLocaleString(),
        filterType: "Exact",
      },
      {
        Header: "Updated Time",
        accessor: "updated_time",
        Cell: ({ value }) => new Date(value).toLocaleString(),
        filterType: "Exact",
      },
      {
        Header: "Uploader",
        accessor: "uploader",
        filterType: "Exact",
      },
      {
        Header: "Actions",
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ row }) => {
          const patient = row.original;
          return (
            <div className="actions-container">
              <Link style={{ textDecoration: "none" }} to={`/patients/${patient.id}`}>
                <button className="action-btn view">
                  <i className="fas fa-eye"></i> View
                </button>
              </Link>
              <Link style={{ textDecoration: "none" }} to={`/patients/${patient.id}/edit`}>
                <button className="action-btn edit">
                  <i className="fas fa-edit"></i> Edit
                </button>
              </Link>
              <button
                className="action-btn data"
                onClick={() => openModalForPatient(patient.id)}
              >
                <i className="fas fa-database"></i> Data
              </button>
            </div>
          );
        },
      },
    ],
    []
  );

  // We'll define a default column filter
  const defaultColumn = useMemo(() => ({}), []);

  // 2) Setup useTable
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount: controlledPageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    setAllFilters,
    selectedFlatRows,
  } = useTable(
    {
      columns,
      data: patients,
      defaultColumn, 
      manualPagination: true,
      manualSortBy: true,
      manualFilters: true,
      pageCount: pageCount,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
      },
      autoResetPage: false,
      autoResetSortBy: false,
      autoResetFilters: false,
      autoResetSelectedRows: false,
    },
    useFilters,
    useSortBy,
    usePagination, 
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        {
          id: "selection",
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
          ),
          Cell: ({ row }) => (
            <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
          ),
        },
        ...columns,
      ]);
    }
  );

  const { pageIndex, pageSize, sortBy, filters } = state;

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/patients`,
        {
          params: {
            page: pageIndex + 1,
            page_size: pageSize,
            ...(sortBy?.length > 0
              ? {
                  sort_by: sortBy[0].id,
                  sort_dir: sortBy[0].desc ? "desc" : "asc",
                }
              : {}),
            ...(filters?.length > 0
              ? filters.reduce((acc, filter) => {
                  if (filter.value) acc[filter.id] = filter.value;
                  return acc;
                }, {})
              : {}),
          },
          headers: { Authorization: `Token ${token}` },
        }
      );
      
      const { results, pageCount: serverPageCount } = res.data;
      setPatients(results || []);
      setPageCount(serverPageCount || 0);
      
      if (!results || !results.length) {
        setPlaceholder("No patients found.");
      } else {
        setPlaceholder("");
      }
    } catch (error) {
      console.error("Failed to fetch patients:", error);
      setPatients([]);
      setPageCount(0);
    } finally {
      setLoading(false);
    }
  }, [pageIndex, pageSize, sortBy, filters, token]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients, refreshData]);

  useEffect(() => {
    if (selectedFlatRows && onSelectedIdsChange) {
      const selectedIds = selectedFlatRows
        .map(row => patients[row.index]?.id)
        .filter(id => id); // Filter out any undefined
      
      onSelectedIdsChange(selectedIds);
    }
  }, [selectedFlatRows, patients, onSelectedIdsChange]);

  // 4) Render
  if (loading) {
    return <div>Loading patients...</div>;
  }

  if (!page.length && placeholder) {
    return (
      <>
        <div className="placeholder-message">
          {placeholder} {" "}
          {filters.length > 0 && (
            <button className="filter-button" onClick={() => setAllFilters([])}>Clear Filters</button>
          )}
        </div>
      </>
    );
  }

  return (
    <div className="patients-table-container">
      <div className="table-scrollable">
        <div className="table-actions">
          <button
            className="filter-button"
            onClick={() => setFilterModalOpen(true)}
          >
            Filter Data
          </button>
        </div>
        <table {...getTableProps()} className="patients-table">
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key: hgKey, ...restHG } =
                headerGroup.getHeaderGroupProps();
              return (
                <tr key={hgKey} {...restHG}>
                  {headerGroup.headers.map((column) => {
                    const { key: colKey, ...restHeader } =
                      column.getHeaderProps();
                    return (
                      <th key={colKey} {...restHeader}>
                        {/* 1) The sorting label div */}
                        <div
                          {...(column.getSortByToggleProps
                            ? column.getSortByToggleProps()
                            : {})}
                          style={{
                            cursor: column.disableSortBy
                              ? "default"
                              : "pointer",
                          }}
                        >
                          {column.render("Header")}
                          {column.isSorted
                            ? column.isSortedDesc
                              ? " ▼"
                              : " ▲"
                            : ""}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              const { key: rowKey, ...restRow } = row.getRowProps();
              return (
                <tr key={rowKey} {...restRow}>
                  {row.cells.map((cell) => {
                    const { key: cellKey, ...restCell } = cell.getCellProps();
                    return (
                      <td key={cellKey} {...restCell} data-column={cell.column.id}>
                        {cell.render("Cell")}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination UI */}
      <div className="pagination-container">
        <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
          {"<<"}
        </button>
        <button onClick={() => previousPage()} disabled={!canPreviousPage}>
          {"<"}
        </button>
        <button onClick={() => nextPage()} disabled={!canNextPage}>
          {">"}
        </button>
        <button onClick={() => gotoPage(controlledPageCount - 1)} disabled={!canNextPage}>
          {">>"}
        </button>
        <span>
          Page{" "}
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>
        </span>
        <select
          value={pageSize}
          style={{ width: "auto" }}
          onChange={(e) => setPageSize(Number(e.target.value))}
        >
          {[10, 20, 50, 100].map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>
      </div>

      {/* Data Navigation Modal */}
      <DataNavigationModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        contextType="patient"
        resourceIds={{ patientId: modalResourceId }}
      />

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        columns={columns.filter((col) => !col.disableFilters)}
        currentFilters={state.filters}
        onApplyFilters={(filters) => {
          // This will trigger a re-fetch with the new filters
          setAllFilters(filters);
        }}
      />
    </div>
  );
};

export default PatientsTable;
